import { Link } from 'react-router-dom';
import { ArrowUpRight, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useMutation } from 'convex/react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';

interface ProjectCardProps {
  project: {
    _id: Id<'projects'>;
    name: string;
    description: string | null;
    tags: string[];
    status: string;
    currentJobId: Id<'jobs'> | null;
    lastActivityAt: number;
    createdAt: number;
  };
}

/**
 * Decide where "Open" should go based on status and the linked job, if any.
 */
function destinationFor(
  status: string,
  projectId: Id<'projects'>,
  currentJobId: Id<'jobs'> | null,
): string {
  if (currentJobId) {
    if (status === 'pipeline' || status === 'failed') {
      return ROUTES.jobPipeline(currentJobId);
    }
    if (status === 'completed') {
      return ROUTES.jobDelivery(currentJobId);
    }
  }
  return ROUTES.projectWizard(projectId);
}

function formatRelative(ts: number): string {
  const now = Date.now();
  const seconds = Math.floor((now - ts) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProjectCard({ project }: ProjectCardProps) {
  const { token } = useAuth();
  const remove = useMutation(api.projects.remove);
  const { toast } = useToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = async () => {
    if (!token) return;
    setDeleting(true);
    try {
      await remove({ token, projectId: project._id });
      toast({ title: 'Project deleted', description: project.name });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not delete',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const destination = destinationFor(
    project.status,
    project._id,
    project.currentJobId,
  );

  return (
    <article
      className={cn(
        'group relative flex flex-col gap-4 border border-ink/15 bg-paper p-6 transition-colors',
        'hover:border-ink/40',
      )}
    >
      {/* Top row: status + menu */}
      <div className="flex items-start justify-between gap-3">
        <ProjectStatusBadge status={project.status} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="-mr-2 -mt-2 h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Project actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setConfirmingDelete(true);
              }}
              className="text-vermilion focus:text-vermilion"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete project
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Title + description */}
      <Link to={destination} className="block">
        <h3 className="font-serif text-2xl italic leading-tight text-ink transition-colors group-hover:text-ochre">
          {project.name}
        </h3>
        {project.description ? (
          <p className="mt-2 line-clamp-3 text-sm text-graphite leading-relaxed">
            {project.description}
          </p>
        ) : (
          <p className="mt-2 text-sm italic text-graphite/60">No description yet.</p>
        )}
      </Link>

      {/* Tags */}
      {project.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {project.tags.map((t) => (
            <span
              key={t}
              className="rounded-sm border border-ink/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-graphite"
            >
              {t}
            </span>
          ))}
        </div>
      ) : null}

      {/* Footer: timestamp + open CTA */}
      <div className="mt-auto flex items-center justify-between border-t border-ink/10 pt-3">
        <p className="font-mono text-[11px] text-graphite">
          updated {formatRelative(project.lastActivityAt)}
        </p>
        <Link
          to={destination}
          className="inline-flex items-center gap-1 font-mono text-xs text-ink transition-colors hover:text-ochre"
        >
          Open <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Inline delete confirmation overlay */}
      {confirmingDelete ? (
        <div
          role="dialog"
          aria-modal="true"
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 border border-vermilion/40 bg-paper/95 p-6 backdrop-blur-sm"
        >
          <p className="font-serif text-lg italic text-ink">Delete this project?</p>
          <p className="text-center text-xs text-graphite leading-relaxed">
            This deletes the questionnaire, sketches, agent outputs, and
            generated artefacts. It cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
